import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { formatPrice } from '@/services/stripe';
import { isInTrialPeriod, getTrialDaysRemaining, getTrialStatusMessage } from '@/utils/subscription-trial';
import { CalendarClock, AlertCircle } from 'lucide-react';

interface SubscriptionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface SubscriptionData {
  id: string;
  status: string;
  current_period_end: number;
  cancel_at_period_end: boolean;
  trial_start?: number;
  trial_end?: number;
  plan: {
    id: string;
    nickname: string;
    amount: number;
    currency: string;
    interval: string;
    interval_count: number;
  };
}

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({ 
  open, 
  onOpenChange 
}) => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && user) {
      fetchSubscriptionData();
    }
  }, [open, user]);

  const fetchSubscriptionData = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.functions.invoke('get-stripe-data/subscription', {
        body: {
          customerId: user?.id,
          email: user?.email
        }
      });

      if (error) {
        console.error('Error fetching subscription data:', error);
        setError('Failed to load subscription data. Please try again.');
      } else if (data?.subscription) {
        setSubscription(data.subscription);
      } else {
        setSubscription(null);
      }
    } catch (err) {
      console.error('Unexpected error fetching subscription:', err);
      setError('An unexpected error occurred. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'trialing':
        return 'bg-blue-100 text-blue-800';
      case 'past_due':
        return 'bg-yellow-100 text-yellow-800';
      case 'canceled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Subscription Details</DialogTitle>
          <DialogDescription>
            View your current subscription information
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : error ? (
            <div className="text-center py-4 text-red-500">
              <p>{error}</p>
              <Button 
                variant="outline" 
                className="mt-2" 
                onClick={fetchSubscriptionData}
              >
                Try Again
              </Button>
            </div>
          ) : !subscription ? (
            <div className="text-center py-4">
              <p className="text-muted-foreground">You don't have an active subscription.</p>
              <Button 
                className="mt-4" 
                onClick={() => window.location.href = '/pricing'}
              >
                View Plans
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="font-medium">Status</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(subscription.status)}`}>
                  {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                </span>
              </div>

              {/* Trial information */}
              {(subscription.trial_end || subscription.status === 'trialing') && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-blue-800 text-sm">
                  <div className="flex items-start">
                    <CalendarClock className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                    <div>
                      {subscription.status === 'trialing' ? (
                        <>
                          <p className="font-medium">Premium Trial Active</p>
                          <p className="mt-1">
                            Your trial will end on {subscription.trial_end ? formatDate(subscription.trial_end) : 'soon'}.
                            {subscription.trial_end && (
                              <>
                                {' '}{getTrialDaysRemaining({trial_end: new Date(subscription.trial_end * 1000).toISOString()} as any) === 0 
                                  ? 'Your trial ends today!' 
                                  : `You have ${getTrialDaysRemaining({trial_end: new Date(subscription.trial_end * 1000).toISOString()} as any)} days remaining.`
                                }
                              </>
                            )}
                          </p>
                        </>
                      ) : subscription.trial_end && new Date(subscription.trial_end * 1000) < new Date() ? (
                        <>
                          <p className="font-medium">Trial Completed</p>
                          <p className="mt-1">Your trial ended on {formatDate(subscription.trial_end)}. You're now on a regular billing cycle.</p>
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-between">
                <span className="font-medium">Plan</span>
                <span>{subscription.plan.nickname || 'Standard Plan'}</span>
              </div>

              <div className="flex justify-between">
                <span className="font-medium">Price</span>
                <span>
                  {formatPrice({
                    id: subscription.plan.id,
                    productId: '',
                    unitAmount: subscription.plan.amount,
                    currency: subscription.plan.currency,
                    recurring: {
                      interval: subscription.plan.interval as any,
                      intervalCount: subscription.plan.interval_count
                    },
                    active: true
                  })}
                  {' / '}
                  {subscription.plan.interval === 'month' && subscription.plan.interval_count === 6 
                    ? 'semi-annual' 
                    : subscription.plan.interval}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="font-medium">Current Period Ends</span>
                <span>{formatDate(subscription.current_period_end)}</span>
              </div>

              {subscription.cancel_at_period_end && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-amber-800 text-sm">
                  <div className="flex items-start">
                    <AlertCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium">{subscription.status === 'trialing' ? 'Trial Auto-Renewal Canceled' : 'Subscription Ending'}</p>
                      <p className="mt-1">
                        {subscription.status === 'trialing' 
                          ? `Your trial will continue until ${formatDate(subscription.current_period_end)}, but will not convert to a paid subscription.` 
                          : 'Your subscription is set to cancel at the end of the current billing period.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="sm:justify-start">
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
          {subscription && (subscription.status === 'active' || subscription.status === 'trialing') && !subscription.cancel_at_period_end && (
            <Button
              type="button"
              variant="outline"
              className="text-red-500 hover:text-red-700 hover:bg-red-50"
              onClick={async () => {
                const isTrial = subscription.status === 'trialing';
                const confirmMessage = isTrial
                  ? 'Are you sure you want to cancel auto-renewal? You will still have access until your trial ends.'
                  : 'Are you sure you want to cancel your subscription? You will still have access until the end of your current billing period.';
                
                if (confirm(confirmMessage)) {
                  try {
                    setLoading(true);
                    const { data, error } = await supabase.functions.invoke('cancel-subscription', {
                      body: { subscriptionId: subscription.id }
                    });
                    
                    if (error) {
                      console.error('Error cancelling subscription:', error);
                      setError('Failed to cancel subscription. Please try again.');
                    } else {
                      // Refresh subscription data
                      fetchSubscriptionData();
                    }
                  } catch (err) {
                    console.error('Unexpected error cancelling subscription:', err);
                    setError('An unexpected error occurred. Please try again later.');
                  } finally {
                    setLoading(false);
                  }
                }
              }}
            >
              {subscription.status === 'trialing' ? 'Cancel Auto-Renewal' : 'End Subscription'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionModal;